import { SelectableValue } from "../common/selectable-value";
import { useRecoilValue } from "recoil";
import { measureUnits } from "../../store/simulator";

export const createNewProductOption = "create-new-product";
export enum TimeFrameOption {
	FROM = "dd-option-from",
	TO = "dd-option-to",
	ONWARD = "dd-option-onward",
}

export enum BusinessCaseOption {
	BEST_CASE = "business-case-1",
	BASE_CASE = "business-case-2",
	WORST_CASE = "business-case-3",
	BATNA = "business-case-4",
}

export enum VolumeBasedThresholdOption {
	FROM = "FROM",
	TO = "TO",
	GREATER_OR_EQUAL = "GREATER_OR_EQUAL",
}

export enum RegimenScheduleOption {
	FIRST = "FIRST",
	EVERY = "EVERY",
	ONCE = "ONCE",
}

export enum RegimenDurationOption {
	ONWARD = "ONWARD",
	FIRST = "FIRST",
	EVER = "EVER",
}

export enum TimeUnitOption {
	DAY = "DAY",
	WEEK = "WEEK",
	MONTH = "MONTH",
	YEAR = "YEAR",
}

export enum TimeUnitPluralOption {
	DAYS = "DAYS",
	WEEKS = "WEEKS",
	MONTHS = "MONTHS",
	YEARS = "YEARS",
}

export enum IntervalOption {
	GREATER_OR_EQUAL = "GREATER_OR_EQUAL",
	LESS_OR_EQUAL = "LESS_OR_EQUAL",
	FROM = "FROM",
	TO = "TO",
}

export enum RegimenWeightBasedOption {
	MG_KG_OF_PATIENT_WEIGHT = "mg/kg of patient weight",
}

export enum RegimenGroups {
	WEIGHT_BASED = "simulator-regimen-weight",
	PACK_BASED = "simulator-regimen-pack",
}

export enum DiscountOptions {
	PER_PACK = "PER_PACK",
}

export enum IncreasePatternOptions {
	LINEAR = "patient-population-linear",
	NON_LINEAR = "patient-population-non-linear",
}

export enum CostOfContractOptions {
	ONCE = "ONCE",
	ANNUAL = "ANNUAL",
	MONTHLY = "MONTHLY",
}

export enum SimulatorValueScopeOptions {
	CONTRACT_LEVEL = "simulation-level",
	PATIENT_LEVEL = "patient-level",
}

export enum ConditionOperatorOptions {
	IF = "IF",
	IS = "IS",
	WHEN = "WHEN",
}

export enum DiscountUnitOptions {
	PERCENTAGE = "%",
	CURRENCY = "CURRENCY",
}

export enum ModelTypeOptions {
	INDICATION_BASED = "INDICATION_BASED",
	FIXED_FEE_DISCOUNT = "FIXED_FEE_DISCOUNT",
	INGREDIENT_BASED_DISCOUNT = "INGREDIENT_BASED_DISCOUNT",
	OUTCOME_BASED = "OUTCOME_BASED",
	BUDGET_CAP = "BUDGET_CAP",
	FREE_OR_DISCOUNTED_INITIATION = "FREE_OR_DISCOUNTED_INITIATION",
	UTILIZATION_CAP = "UTILIZATION_CAP",
	PRICE_DOSE_CAP = "PRICE_DOSE_CAP",
}

export enum DiscountCoverageEnum {
	STRICT = "strict",
	CONTINUOUS = "continuous",
}

export enum StraightDiscountEnum {
	STRAIGHT_DISCOUNT = "STRAIGHT_DISCOUNT",
}

export enum PatternPackOptionsValue {
	BIGGEST_BELOW = "BIGGEST_BELOW",
	SMALLEST_ABOVE = "SMALLEST_ABOVE",
	CLOSEST = "CLOSEST",
	REUSE_PACK = "REUSE_PACK",
}

export enum PackDosageUnit {
	MG = "mg",
	ML = "ml",
	VIAL = "vial",
	COURSE = "course",
}

export const startTimeFrameOptions: Array<SelectableValue> = [{ label: TimeFrameOption.FROM, value: TimeFrameOption.FROM }];

export const endTimeFrameOptions: Array<SelectableValue> = [
	{ label: TimeFrameOption.TO, value: TimeFrameOption.TO },
	{ label: TimeFrameOption.ONWARD, value: TimeFrameOption.ONWARD },
];

export const treatmentRegimenScheduleOptions = [
	{ label: "treatment-regimen-once", value: RegimenScheduleOption.ONCE },
	{ label: "every", value: RegimenScheduleOption.EVERY },
];
export const treatmentRegimenDurationOptions = [
	{ label: "for-the-first", value: RegimenDurationOption.FIRST },
	{ label: "dd-option-onward", value: RegimenDurationOption.ONWARD },
	{ label: "simulation-ever", value: RegimenDurationOption.EVER },
];
export const unitsCompatibleWithWaste = [PackDosageUnit.MG, PackDosageUnit.ML];
export const dosageStrengthOptions = [
	{ value: PackDosageUnit.MG },
	{ value: PackDosageUnit.ML },
	{ value: PackDosageUnit.COURSE },
	{ value: PackDosageUnit.VIAL },
];

export const discountCoverageOptions = [
	{
		value: DiscountCoverageEnum.STRICT,
		label: "model-validity-behavior-strict",
		description: "model-validity-behavior-strict-description",
	},
	{
		value: DiscountCoverageEnum.CONTINUOUS,
		label: "model-validity-behavior-continuous",
		description: "model-validity-behavior-continuous-description",
	},
];

export const businessCaseOptions = [
	{
		label: BusinessCaseOption.BEST_CASE,
		value: BusinessCaseOption.BEST_CASE,
		backgroundColor: "#e8f3d9",
		backgroundColorHover: "#c6dbb7",
		textColor: "#4c7057",
	},
	{
		label: BusinessCaseOption.BASE_CASE,
		value: BusinessCaseOption.BASE_CASE,
		backgroundColor: "#daddfe",
		backgroundColorHover: "#a8afff",
		textColor: "#614faa",
	},
	{
		label: BusinessCaseOption.WORST_CASE,
		value: BusinessCaseOption.WORST_CASE,
		backgroundColor: "#fff4dc",
		backgroundColorHover: "#ffde9b",
		textColor: "#a87511",
	},
	{
		label: BusinessCaseOption.BATNA,
		value: BusinessCaseOption.BATNA,
		backgroundColor: "#ffecf7",
		backgroundColorHover: "#f5cce5",
		textColor: "#bf3e93",
	},
];

export const treatmentRegimenDurationContinuouslyOption = { label: "dd-option-continuously", value: RegimenDurationOption.ONWARD };

export const treatmentRegimenScheduleUnitOptions = [
	{ label: "days", value: TimeUnitOption.DAY },
	{ label: "weeks", value: TimeUnitOption.WEEK },
	{ label: "months", value: TimeUnitOption.MONTH },
];

export const evaluationUnitOptions = [
	{ label: "weeks", value: TimeUnitOption.WEEK },
	{ label: "months", value: TimeUnitOption.MONTH },
];

export const patientWeightIntervalOptions = [
	{ label: "dd-option-less-or-equal", value: IntervalOption.LESS_OR_EQUAL },
	{ label: "dd-option-from", value: IntervalOption.FROM },
	{ label: "dd-option-to", value: IntervalOption.TO },
	{ label: "dd-option-more-or-equal", value: IntervalOption.GREATER_OR_EQUAL },
];

export const treatmentDurationOptions = [
	{ label: "months", value: TimeUnitOption.MONTH },
	{ label: "weeks", value: TimeUnitOption.WEEK },
	{ label: "days", value: TimeUnitOption.DAY },
];

export const onlyDaysOption = [{ label: "days", value: TimeUnitOption.DAY }];

export const weightBasedOption = {
	label: "simulator-uom-mg-kg",
	value: RegimenWeightBasedOption.MG_KG_OF_PATIENT_WEIGHT,
	groupBy: RegimenGroups.WEIGHT_BASED,
};

export const straightDiscountOptions = [{ label: "scenario-per-pack", value: DiscountOptions.PER_PACK }];

export enum VolumeBaseOptionLabels {
	PATIENT_LEVEL = "PATIENT LEVEL",
	SIMULATION_LEVEL = "SIMULATION LEVEL",
}

export enum VolumeMeasureOptionLabels {
	ACTIVE_INGREDIENT = "model-volume-ingredient",
	TREATMENT_COST = "model-volume-amount",
	QUANTITY = "model-volume-pack",
	PATIENTS = "model-patient-count",
}

export enum FreeOrDiscountedInitiationScopeOptionLabels {
	PATIENT_LEVEL = "PATIENT LEVEL",
}

export enum FreeOrDiscountedInitiationOptionLabels {
	DAYS_OF_TREATMENT = "model-volume-days",
	PACK_QUANTITY = "model-volume-pack",
	ACTIVE_INGREDIENT_QUANTITY = "model-volume-ingredient",
}

export enum FreeOrDiscountedInitiationDiscountMethodOptionLabels {
	INITIATION_PERIOD = "model-volume-initiation-period",
}

export enum FreeOrDiscountedInitiationDiscountMethodOptionValues {
	INITIATION_PERIOD = "INITIATION_PERIOD",
}

export enum FreeOrDiscountedInitiationPeriodOptionLabels {
	TREATMENT_YEAR = "scenario-treatment-year",
	TREATMENT_COURSE = "scenario-treatment-course",
}

export enum DiscountMethodOptionLabels {
	PROSPECTIVELY = "model-volume-prospectively",
	RETROACTIVELY = "model-volume-retroactively",
}

export enum OutcomeMeasureTypeLabels {
	OTHER = "outcome-measure-type-other-name",
	TIME_TO_TREATMENT_DISCONTINUATION = "outcome-measure-type-time-name",
}

export enum DiscountMethodOptionDescriptions {
	PROSPECTIVELY = "model-volume-prospectively-note",
	RETROACTIVELY = "model-volume-retroactively-note",
}

export enum OutcomeMeasureTypeDescriptions {
	OTHER = "outcome-measure-type-other-description",
	TIME_TO_TREATMENT_DISCONTINUATION = "outcome-measure-type-time-description",
}

export enum DiscountMeasuringPeriodOptionLabels {
	CALENDAR_YEAR = "scenario-calendar-year",
	FISCAL_YEAR = "fiscal-year-start",
	TREATMENT_YEAR = "scenario-treatment-year",
	TREATMENT_COURSE = "scenario-treatment-course",
}

export const FreeOrDiscountedInitiationDiscountMethodOptions = [
	{
		label: FreeOrDiscountedInitiationDiscountMethodOptionLabels.INITIATION_PERIOD,
		value: FreeOrDiscountedInitiationDiscountMethodOptionValues.INITIATION_PERIOD,
	},
];

export const freeOrDiscountedInitiationScopeOptions = [
	{
		label: "patient-level",
		value: VolumeBaseOptionLabels.PATIENT_LEVEL,
	},
];
export enum MonthsSelectionOptionLabels {
	JANUARY = "january",
	FEBRUARY = "february",
	MARCH = "march",
	APRIL = "april",
	MAY = "may",
	JUNE = "june",
	JULY = "july",
	AUGUST = "august",
	SEPTEMBER = "september",
	OCTOBER = "october",
	NOVEMBER = "november",
	DECEMBER = "december",
}

export const volumeBasedOptions = [
	{
		label: "patient-level",
		value: VolumeBaseOptionLabels.PATIENT_LEVEL,
	},
	{
		label: "simulation-level",
		value: VolumeBaseOptionLabels.SIMULATION_LEVEL,
	},
];

export const volumeMeasureOptions = [
	{
		label: VolumeMeasureOptionLabels.ACTIVE_INGREDIENT,
		value: VolumeMeasureOptionLabels.ACTIVE_INGREDIENT,
	},
	{
		label: VolumeMeasureOptionLabels.TREATMENT_COST,
		value: VolumeMeasureOptionLabels.TREATMENT_COST,
	},
	{
		label: VolumeMeasureOptionLabels.QUANTITY,
		value: VolumeMeasureOptionLabels.QUANTITY,
	},
	{
		label: VolumeMeasureOptionLabels.PATIENTS,
		value: VolumeMeasureOptionLabels.PATIENTS,
	},
];

export const measureOptions = [
	{
		label: FreeOrDiscountedInitiationOptionLabels.DAYS_OF_TREATMENT,
		value: FreeOrDiscountedInitiationOptionLabels.DAYS_OF_TREATMENT,
	},
	{
		label: FreeOrDiscountedInitiationOptionLabels.PACK_QUANTITY,
		value: FreeOrDiscountedInitiationOptionLabels.PACK_QUANTITY,
	},
	{
		label: FreeOrDiscountedInitiationOptionLabels.ACTIVE_INGREDIENT_QUANTITY,
		value: FreeOrDiscountedInitiationOptionLabels.ACTIVE_INGREDIENT_QUANTITY,
	},
];

export const DiscountMethodOptions = [
	{
		label: DiscountMethodOptionLabels.PROSPECTIVELY,
		description: DiscountMethodOptionDescriptions.PROSPECTIVELY,
		value: DiscountMethodOptionLabels.PROSPECTIVELY,
	},
	{
		label: DiscountMethodOptionLabels.RETROACTIVELY,
		description: DiscountMethodOptionDescriptions.RETROACTIVELY,
		value: DiscountMethodOptionLabels.RETROACTIVELY,
	},
];

export const OutcomeMeasureTypeOptions = [
	{
		label: OutcomeMeasureTypeLabels.TIME_TO_TREATMENT_DISCONTINUATION,
		description: OutcomeMeasureTypeDescriptions.TIME_TO_TREATMENT_DISCONTINUATION,
		value: OutcomeMeasureTypeLabels.TIME_TO_TREATMENT_DISCONTINUATION,
	},
	{
		label: OutcomeMeasureTypeLabels.OTHER,
		description: OutcomeMeasureTypeDescriptions.OTHER,
		value: OutcomeMeasureTypeLabels.OTHER,
	},
];

export const VolumeBasedThresholdOptions = [
	{
		label: "dd-option-from",
		value: VolumeBasedThresholdOption.FROM,
	},
	{
		label: "dd-option-to",
		value: VolumeBasedThresholdOption.TO,
	},
	{
		label: "dd-option-more-or-equal",
		value: VolumeBasedThresholdOption.GREATER_OR_EQUAL,
	},
];

export const DiscountMeasuringPeriodOptions = [
	{
		label: DiscountMeasuringPeriodOptionLabels.CALENDAR_YEAR,
		value: DiscountMeasuringPeriodOptionLabels.CALENDAR_YEAR,
	},
	{
		label: "scenario-fiscal-year",
		value: DiscountMeasuringPeriodOptionLabels.FISCAL_YEAR,
	},
	{
		label: DiscountMeasuringPeriodOptionLabels.TREATMENT_YEAR,
		value: DiscountMeasuringPeriodOptionLabels.TREATMENT_YEAR,
	},
	{
		label: DiscountMeasuringPeriodOptionLabels.TREATMENT_COURSE,
		value: DiscountMeasuringPeriodOptionLabels.TREATMENT_COURSE,
	},
];

export const MonthsOptions = [
	{
		label: MonthsSelectionOptionLabels.JANUARY,
		value: MonthsSelectionOptionLabels.JANUARY,
	},
	{
		label: MonthsSelectionOptionLabels.FEBRUARY,
		value: MonthsSelectionOptionLabels.FEBRUARY,
	},
	{
		label: MonthsSelectionOptionLabels.MARCH,
		value: MonthsSelectionOptionLabels.MARCH,
	},
	{
		label: MonthsSelectionOptionLabels.APRIL,
		value: MonthsSelectionOptionLabels.APRIL,
	},
	{
		label: MonthsSelectionOptionLabels.MAY,
		value: MonthsSelectionOptionLabels.MAY,
	},
	{
		label: MonthsSelectionOptionLabels.JUNE,
		value: MonthsSelectionOptionLabels.JUNE,
	},
	{
		label: MonthsSelectionOptionLabels.JULY,
		value: MonthsSelectionOptionLabels.JULY,
	},
	{
		label: MonthsSelectionOptionLabels.AUGUST,
		value: MonthsSelectionOptionLabels.AUGUST,
	},
	{
		label: MonthsSelectionOptionLabels.SEPTEMBER,
		value: MonthsSelectionOptionLabels.SEPTEMBER,
	},
	{
		label: MonthsSelectionOptionLabels.OCTOBER,
		value: MonthsSelectionOptionLabels.OCTOBER,
	},
	{
		label: MonthsSelectionOptionLabels.NOVEMBER,
		value: MonthsSelectionOptionLabels.NOVEMBER,
	},
	{
		label: MonthsSelectionOptionLabels.DECEMBER,
		value: MonthsSelectionOptionLabels.DECEMBER,
	},
];

export const getDiscountUnitOptions = () => {
	const { currency } = useRecoilValue(measureUnits);
	return [
		{ label: DiscountUnitOptions.PERCENTAGE, value: DiscountUnitOptions.PERCENTAGE },
		{ label: currency, value: DiscountUnitOptions.CURRENCY },
	];
};

export const InitiationThresholdOptions = [
	{
		label: "dd-option-from",
		value: VolumeBasedThresholdOption.FROM,
	},
	{
		label: "dd-option-to",
		value: VolumeBasedThresholdOption.TO,
	},
];

export const packOptions = [
	{
		label: "simulator-regimen-pattern-biggest-below",
		value: PatternPackOptionsValue.BIGGEST_BELOW,
		description: "simulator-regimen-pattern-biggest-below-descr",
	},
	{
		label: "simulator-regimen-pattern-smallest-above",
		value: PatternPackOptionsValue.SMALLEST_ABOVE,
		description: "simulator-regimen-pattern-smallest-above-descr",
	},
	{
		label: "simulator-regimen-pattern-closest-descr",
		value: PatternPackOptionsValue.CLOSEST,
		description: "simulator-regimen-pattern-closest",
	},
	{
		label: "simulator-regimen-pattern-reuse",
		value: PatternPackOptionsValue.REUSE_PACK,
		description: "simulator-regimen-pattern-reuse-description",
	},
];

export enum SimulationModelsEnumDev {
	IndicationBasedPricing = "rec9GB39waKJTD5nL",
	FixedDiscount = "recJVJPIk8cZxDTqK",
	OutcomeBased = "recf9UzwHIefV5oYr",
	IngredientBasedPricing = "recqy2gbR5qvnb61i",
	BudgetCap = "recMPVcx5W2epYGIq",
	UtilizationCap = "recobgtWRE2VRTgXe",
	PriceDoseCap = "rec8xJd2wc8oQbUTj",
	FreeInitiation = "rec9ngdlnQNptUmY4",
}

export enum SimulationModelsEnumProd {
	IndicationBasedPricing = "recbe1epfRUoyHwpO",
	FixedDiscount = "rec30yJvjDg26aBl0",
	OutcomeBased = "recQYst9fgDez7FGn",
	IngredientBasedPricing = "recPaTKpIPFIbuSu4",
	BudgetCap = "recCYJbq9SivNO9On",
	UtilizationCap = "recCDF1OuHckRlRwC",
	PriceDoseCap = "recuPiMQ05lzWitnL",
	FreeInitiation = "recSnraQx9qU0tSQs",
}

export enum RoundingOptionLabels {
	NO_ROUNDING = "outputs-rounding-none",
	ROUND_TO_THOUSANDS = "outputs-rounding-thousands",
	ROUND_TO_MILLIONS = "outputs-rounding-millions",
}

export const RoundingOptions = [
	{
		label: RoundingOptionLabels.NO_ROUNDING,
		value: RoundingOptionLabels.NO_ROUNDING,
	},
	{
		label: RoundingOptionLabels.ROUND_TO_THOUSANDS,
		value: RoundingOptionLabels.ROUND_TO_THOUSANDS,
	},
	{
		label: RoundingOptionLabels.ROUND_TO_MILLIONS,
		value: RoundingOptionLabels.ROUND_TO_MILLIONS,
	},
];
export const nestedModelPrefix = "nested-";

export const modelCombinationConfig = {
	[ModelTypeOptions.INDICATION_BASED]: [
		SimulationModelsEnumDev.OutcomeBased,
		SimulationModelsEnumProd.OutcomeBased,
		SimulationModelsEnumDev.IngredientBasedPricing,
		SimulationModelsEnumProd.IngredientBasedPricing,
		SimulationModelsEnumDev.BudgetCap,
		SimulationModelsEnumProd.BudgetCap,
		SimulationModelsEnumDev.PriceDoseCap,
		SimulationModelsEnumProd.PriceDoseCap,
		SimulationModelsEnumDev.UtilizationCap,
		SimulationModelsEnumProd.UtilizationCap,
		SimulationModelsEnumDev.FreeInitiation,
		SimulationModelsEnumProd.FreeInitiation,
	],
	[nestedModelPrefix + ModelTypeOptions.OUTCOME_BASED]: [],
};

export enum DistributionValue {
	MONTH = "month",
	QUARTER = "quarter",
	YEAR = "year",
}

export const VolumeBasedModels = [
	ModelTypeOptions.INGREDIENT_BASED_DISCOUNT,
	ModelTypeOptions.BUDGET_CAP,
	ModelTypeOptions.PRICE_DOSE_CAP,
	ModelTypeOptions.UTILIZATION_CAP,
];

export const MAX_PATIENT_POPULATION_VALUE = 999999999;
